// ── Helpers ───────────────────────────────────────────────────────────────────

function resolveFieldRefDisplay (fieldRefConfig, fields, segmentFields, entitySingleOwner = false) {
  const sizeTranslations = {
    tiny: 'col25',
    small: 'col33',
    medium: 'col50',
    large: 'col100'
  };
  const sizeRemTranslations = {
    tiny: 6,
    small: 6,
    medium: 9,
    large: 12
  };

  let fieldName = null;
  let labelOverride = null;
  let requiredOverride = null;
  let sizeRem = null;
  let defaultOrder = null;
  let initialOrder = 'asc';
  let disableOrder = false;
  if (typeof (fieldRefConfig) === 'object') {
    if (
      fieldRefConfig.type === 'date_range' ||
      (fieldRefConfig.type === 'field' && fieldRefConfig.field === 'date_range')
    ) {
      fieldName = 'date_range';
      labelOverride = {
        start_label: fieldRefConfig.start_label,
        end_label: fieldRefConfig.end_label
      };
      requiredOverride = fieldRefConfig.required || null;
      sizeRem = Number(fieldRefConfig.size_rem) || null;
      defaultOrder = fieldRefConfig.default_order || null;
      initialOrder = fieldRefConfig.initial_order || 'asc';
    } else if (
      fieldRefConfig.type === 'owners' ||
      (fieldRefConfig.type === 'field' && fieldRefConfig.name === 'owners')
    ) {
      fieldName = 'owners';
      labelOverride = fieldRefConfig.label || null;
      requiredOverride = fieldRefConfig.required || null;
      sizeRem = Number(fieldRefConfig.size_rem) || null;
      defaultOrder = fieldRefConfig.default_order || null;
      initialOrder = fieldRefConfig.initial_order || 'asc';
      disableOrder = fieldRefConfig.disable_order || false;
    } else if (fieldRefConfig.type === 'field') {
      fieldName = fieldRefConfig.field || null;
      labelOverride = fieldRefConfig.label || null;
      requiredOverride = fieldRefConfig.required || null;
      sizeRem = Number(fieldRefConfig.size_rem) || null;
      defaultOrder = fieldRefConfig.default_order || null;
      initialOrder = fieldRefConfig.initial_order || 'asc';
      disableOrder = fieldRefConfig.disable_order || false;
    }
  } else {
    fieldName = fieldRefConfig;
  }

  if (!fieldName) {
    return null;
  }

  const fieldConfig = fields[fieldName];
  if (fieldConfig) {
    // These fields can't be disabled
    if (fieldName === 'id') {
      return {
        ...fieldConfig,
        field: fieldName,
        name: fieldConfig.name || fieldName,
        label: labelOverride || fieldConfig.label || 'ID',
        type: 'IntegerField',
        sizeRem: sizeRem || 4,
        defaultOrder,
        initialOrder,
        disableOrder,
        style: sizeTranslations[fieldConfig.size] || 'col25'
      };
    }
    if (fieldName === 'name') {
      return {
        ...fieldConfig,
        field: fieldName,
        name: fieldConfig.name || fieldName,
        label: labelOverride || fieldConfig.label || 'Name',
        type: 'CharField',
        sizeRem: sizeRem || 12,
        defaultOrder,
        initialOrder,
        disableOrder,
        style: sizeTranslations[fieldConfig.size] || 'col75'
      };
    }
    if (fieldName === 'date_range') {
      // This is disable depending on specific conditions
      if (fieldConfig.disable_date && fieldConfig.disable_date_time) {
        // Skip disabled dates
        return null;
      }
      return {
        ...fieldConfig,
        field: fieldName,
        name: fieldConfig.name || fieldName,
        start_label: labelOverride?.start_label || fieldConfig.start_label || 'Start Date',
        end_label: labelOverride?.end_label || fieldConfig.end_label || 'End Date',
        required: requiredOverride || fieldConfig.required,
        sizeRem: sizeRem || 12,
        defaultOrder,
        initialOrder,
        disableOrder,
        showAllDay: !fieldConfig.disable_date_time && !fieldConfig.disable_date,
        show_time: fieldRefConfig.show_time,
        start: fieldRefConfig.start ?? true,
        end: fieldRefConfig.end ?? true,
        style: 'col100'
      };
    }
    // Every other fields are disabled if disabled is true
    if (fieldConfig.disabled) {
      return null;
    }
    if (fieldName === 'generates_overlaps') {
      if (fieldConfig.disabled ?? true) {
        return null;
      }
      return {
        ...fieldConfig,
        field: fieldName,
        name: fieldConfig.name || fieldName,
        label: labelOverride || fieldConfig.label || 'Generate Overlap',
        type: 'BooleanField',
        required: requiredOverride || fieldConfig.required,
        sizeRem: sizeRem || sizeRemTranslations[fieldConfig.size] || 12,
        defaultOrder,
        initialOrder,
        disableOrder,
        style: sizeTranslations[fieldConfig.size] || 'medium'
      };
    }
    if (fieldName === 'show_public') {
      const display = {
        field: fieldName,
        name: fieldConfig.name || fieldName,
        label: labelOverride || 'Display on Public Viewer',
        type: 'BooleanField',
        data: {},
        dataCheckError: fieldConfig.category_restriction_message,
        dataCheckErrorStyle: 'toggleError',
        dataCheckValue: false,
        dataField: fieldConfig.category_restrictions || [],
        dataExtract: 'is_public',
        dataRelated: fieldConfig.category_restrictions || [],
        default: true,
        sizeRem: sizeRem || 12,
        defaultOrder,
        initialOrder,
        disableOrder,
        style: sizeTranslations[fieldConfig.size] || 'col100',
        componentType: 'toggle'
      };
      display.dataField.forEach(category => {
        const categoryName = category[0] === '|' ? category.slice(1) : category;
        display.data[category] = categoryName;
      });
      return display;
    }
    if (fieldName === 'agency') {
      return {
        ...fieldConfig,
        field: fieldName,
        name: fieldConfig.name || fieldName,
        label: labelOverride || fieldConfig.label || 'Agency',
        required: requiredOverride || fieldConfig.required,
        sizeRem: sizeRem || sizeRemTranslations[fieldConfig.size] || 12,
        defaultOrder,
        initialOrder,
        disableOrder,
        style: sizeTranslations[fieldConfig.size] || 'col100'
      };
    }
    if (fieldName === 'owners') {
      return {
        ...fieldConfig,
        field: fieldName,
        name: fieldConfig.name || fieldName,
        label: labelOverride || fieldConfig.label || (entitySingleOwner ? 'Owner' : 'Owners'),
        required: requiredOverride || fieldConfig.required,
        sizeRem: sizeRem || sizeRemTranslations[fieldConfig.size] || 12,
        defaultOrder,
        initialOrder,
        disableOrder,
        style: sizeTranslations[fieldConfig.size] || 'col100'
      };
    }
    return {
      ...fieldConfig,
      field: fieldName,
      name: fieldConfig.name || fieldName,
      label: labelOverride || fieldConfig.label || fieldName,
      required: requiredOverride || fieldConfig.required,
      sizeRem: sizeRem || sizeRemTranslations[fieldConfig.size],
      defaultOrder,
      initialOrder,
      disableOrder,
      style: sizeTranslations[fieldConfig.size] || ''
    };
  }
  const segmentFieldConfig = segmentFields[fieldName];
  if (segmentFieldConfig) {
    if (segmentFieldConfig.disabled) {
      return [];
    }
    return {
      ...segmentFieldConfig,
      label: labelOverride || segmentFieldConfig.label || fieldName,
      required: requiredOverride || segmentFieldConfig.required,
      sizeRem: sizeRem || sizeRemTranslations[segmentFieldConfig.size],
      defaultOrder,
      initialOrder,
      disableOrder,
      style: sizeTranslations[segmentFieldConfig.size] || ''
    };
  }
  // Support built-ins even if they don't have customized field config
  if (fieldName === 'id') {
    return {
      field: fieldName,
      name: fieldName,
      label: labelOverride || 'ID',
      type: 'IntegerField',
      sizeRem: sizeRem || 6,
      defaultOrder,
      initialOrder,
      disableOrder,
      style: 'col25'
    };
  }
  if (fieldName === 'name') {
    return {
      field: fieldName,
      name: fieldName,
      label: labelOverride || 'Name',
      type: 'CharField',
      disabled: false,
      required: true,
      sizeRem: sizeRem || 12,
      defaultOrder,
      initialOrder,
      disableOrder,
      style: 'col75'
    };
  }
  if (fieldName === 'agency') {
    return {
      field: fieldName,
      name: fieldName,
      label: labelOverride || 'Agency',
      disabled: false,
      required: requiredOverride || false,
      sizeRem: sizeRem || 12,
      defaultOrder,
      initialOrder,
      disableOrder,
      style: 'col100'
    };
  }
  if (fieldName === 'owners') {
    return {
      field: fieldName,
      name: fieldName,
      label: labelOverride || (entitySingleOwner ? 'Owner' : 'Owners'),
      placeholder: 'Select Owner',
      disabled: false,
      required: requiredOverride || false,
      sizeRem: sizeRem || 12,
      defaultOrder,
      initialOrder,
      disableOrder,
      style: 'col100'
    };
  }
  if (fieldName === 'date_range') {
    return {
      field: fieldName,
      name: fieldName,
      start_label: labelOverride?.start_label || 'Start Date',
      end_label: labelOverride?.end_label || 'End Date',
      disable_date: false,
      disable_date_time: false,
      required: requiredOverride || false,
      additionalLabel: labelOverride,
      sizeRem: sizeRem || 12,
      defaultOrder,
      initialOrder,
      disableOrder,
      style: 'col100'
    };
  }

  return null;
}

function resolveFieldFilter (fieldFilterConfig, fields) {
  let fieldName = null;
  let labelOverride = null;
  let placeholder = null;
  if (typeof (fieldFilterConfig) === 'object') {
    if (fieldFilterConfig.type === 'field') {
      fieldName = fieldFilterConfig.field || null;
      labelOverride = fieldFilterConfig.label || null;
      placeholder = fieldFilterConfig.placeholder || null;
    }
  } else {
    fieldName = fieldFilterConfig;
  }

  if (!fieldName) {
    return null;
  }

  const fieldConfig = fields[fieldName];
  if (fieldConfig) {
    // These fields can't be disabled so we don't need to check if they are
    if (fieldName === 'id') {
      return {
        ...fieldConfig,
        field: fieldName,
        name: fieldConfig.name || fieldName,
        label: labelOverride || 'ID',
        placeholder: placeholder || 'Search ids',
        type: 'search'
      };
    }
    if (fieldName === 'name') {
      return {
        ...fieldConfig,
        field: fieldName,
        name: fieldConfig.name || fieldName,
        label: labelOverride || fieldConfig.label || fieldName,
        placeholder: placeholder || 'Search name',
        type: 'search'
      };
    }
    // Every other fields are disabled if disabled is true
    if (fieldConfig.disabled) {
      return null;
    }
    // some specific fields that have special legacy handling
    if (fieldName === 'agency') {
      return {
        ...fieldConfig,
        field: fieldName,
        name: fieldConfig.name || fieldName,
        label: labelOverride || fieldConfig.label || fieldName,
        placeholder: placeholder || 'Search agencies',
        type: 'search'
      };
    }
    if (fieldName === 'owners') {
      return {
        ...fieldConfig,
        field: fieldName,
        name: fieldConfig.name || fieldName,
        label: labelOverride || fieldConfig.label || fieldName,
        placeholder: placeholder || 'Search users',
        type: 'search'
      };
    }
    if (fieldName === 'agency_type') {
      return {
        ...fieldConfig,
        field: fieldName,
        name: fieldConfig.name || fieldName,
        label: labelOverride || fieldConfig.label || fieldName,
        type: 'select'
      };
    }
    if (fieldName[0] === '|') {
      return {
        ...fieldConfig,
        field: fieldName,
        name: fieldConfig.name || fieldName,
        label: labelOverride || fieldConfig.label || fieldName,
        type: 'select'
      };
    }
    if (fieldConfig.type === 'BooleanField') {
      return {
        ...fieldConfig,
        field: fieldName,
        name: fieldConfig.name || fieldName,
        label: labelOverride || fieldConfig.label || fieldName,
        values: [{
          id: 1,
          name: fieldConfig.true_label || fieldConfig.label || fieldName
        }],
        type: 'boolean'
      };
    }
    return {
      ...fieldConfig,
      field: fieldName,
      name: fieldConfig.name || fieldName,
      label: labelOverride || fieldConfig.label || fieldName,
      type: 'attribute_search'
    };
  }
  return null;
}

function translateV2ChipsConfig(configV2) {
  const fields = configV2?.fields || {};
  const segmentFields = configV2?.segment_fields || {};
  const chips = configV2?.display?.card?.chips || [];
  const chipsV1 = [];
  chips.forEach(chipConfig => {
    let chipV1;
    let fieldName;
    let hideLabel;
    let labelOverride;
    if (typeof chipConfig === 'object') {
      fieldName = chipConfig.field;
      hideLabel = !chipConfig.label && (chipConfig.hide_label ?? true);
      labelOverride = chipConfig.label;
      chipV1 = {
        autoHide: chipConfig.hide_empty
      };
      if (chipConfig.style) {
        chipV1.style = chipConfig.style;
      }
      if (chipConfig.value_map) {
        chipV1.values = chipConfig.value_map;
      }
    } else {
      fieldName = chipConfig;
      chipV1 = {
        autoHide: false
      };
    }
    const isCategory = fieldName[0] === '|';
    if (fields[fieldName]) {
      if (fields[fieldName].disabled) {
        return;
      }
      chipV1.field = fields[fieldName].name || fieldName;
      if (!hideLabel) {
        chipV1.label = labelOverride || fields[fieldName].label;
      }
    } else if (fieldName === 'id') {
      chipV1.field = 'id';
      if (!hideLabel) {
        chipV1.label = labelOverride || 'ID:';
      }
    } else if (fieldName === 'name') {
      chipV1.field = 'name';
      if (!hideLabel) {
        chipV1.label = labelOverride || 'Name:';
      }
    }
    if (segmentFields[fieldName]) {
      if (segmentFields[fieldName].disabled) {
        return;
      }
      chipV1.field = segmentFields[fieldName].name || fieldName;
      if (!hideLabel) {
        chipV1.label = labelOverride || segmentFields[fieldName].label;
      }
    }
    if (fieldName === 'agency') {
      chipV1.type = 'agency';
    } else if (isCategory) {
      chipV1.type = 'category';
    } else {
      chipV1.type = 'field';
    }
    chipsV1.push(chipV1);
  });
  return chipsV1;
}

function translateV2Fields(configV2) {
  const v2Fields = configV2?.fields || {};
  const convertedFields = {};
  Object.entries(v2Fields).forEach(([key, v2Field]) => {
    if (key.startsWith('|')) {
      // We do not provide category definitions in the legacy fields property
      return;
    }
    const converted = {
      type: v2Field.type,
      serializer_props: {
        label: v2Field.label || v2Fields.name || key
      }
    };
    if (converted.type === 'TextField') {
      converted.type = 'CharField';
    }
    if (converted.type === 'CharField') {
      if (v2Field.required) {
        converted.serializer_props.required = true;
        converted.serializer_props.allow_null = false;
        converted.serializer_props.allow_blank = false;
      } else {
        converted.serializer_props.required = false;
        converted.serializer_props.allow_null = true;
        converted.serializer_props.allow_blank = true;
      }
      const max_length = v2Field.serializer_props?.max_length;
      if (max_length) {
        converted.serializer_props.max_length = max_length;
      }
    } else if (converted.type === 'BooleanField') {
      if (v2Field.required) {
        converted.serializer_props.required = true;
        converted.serializer_props.allow_null = false;
        converted.serializer_props.default = false;
      } else {
        converted.serializer_props.required = false;
        converted.serializer_props.allow_null = true;
      }
      if ((v2Field.default ?? null) !== null) {
        converted.serializer_props.default = v2Field.default;
      }
    } else {
      // eslint-disable-next-line no-lonely-if
      if (v2Field.required) {
        converted.serializer_props.required = true;
        converted.serializer_props.allow_null = false;
      } else {
        converted.serializer_props.required = false;
        converted.serializer_props.allow_null = true;
      }
    }
    convertedFields[key] = converted;
  });
  return convertedFields;
}

function translateV2DetailTrayConfig(configV2, entitySingleOwner = false) {
  const FIELD_TYPE_TRANSLATIONS = {
    BooleanField: 'boolean',
    CharField: 'fulltext',
    DateField: 'date',
    DateTimeField: 'datetime',
    DecimalField: 'fulltext',
    FloatField: 'fulltext',
    IntegerField: 'fulltext',
    TextField: 'blob'
  };
  const fields = configV2?.fields || {};
  const segmentFields = configV2?.segment_fields || {};
  const entityTrayItems = configV2?.display?.tray?.details || [];

  const translateField = (displayConfig) => {
    if (displayConfig.type === 'agency_type') {
      return [{data: [{
        label: displayConfig.label || 'Agency type',
        key: 'agency_type_name'
      }]}];
    }

    if (displayConfig.type === 'all_day') {
      return [{data: [{
        label: displayConfig.label || 'All day',
        key: 'all_day',
        type: 'boolean'
      }]}];
    }

    if (
      displayConfig === 'contact' ||
      displayConfig.type === 'contact'
    ) {
      const label = displayConfig.label || fields.contact?.label || 'Contact';
      return [{key: 'contact', data: [
        {
          label: `${label} name`,
          key: 'name'
        },
        {
          label: `${label} email`,
          key: 'email',
          type: 'email'
        },
        {
          label: `${label} phone`,
          key: 'phone'
        }
      ]}];
    }

    const fieldDisplay = resolveFieldRefDisplay(displayConfig, fields, segmentFields, entitySingleOwner);
    if (!fieldDisplay) {
      return [];
    }
    if (fieldDisplay.name === 'owners') {
      if (fieldDisplay.label) {
        return [
          {data: [{
            label: fieldDisplay.label,
            type: 'header'
          }]},
          {data: [{type: 'owners'}]}
        ];
      }
      return [{data: [{type: 'owners'}]}];
    }
    if (
      fieldDisplay.type === 'date_range' ||
      fieldDisplay.name === 'date_range'
    ) {
      const dateTimeTrayConfig = [
        {data: [
          {
            label: displayConfig.start_label || fieldDisplay.start_label,
            key: 'start_date',
            type: fieldDisplay.disable_date_time ? 'date' : 'datetime'
          },
          {
            label: displayConfig.end_label || fieldDisplay.end_label,
            key: 'end_date',
            type: fieldDisplay.disable_date_time ? 'date' : 'datetime'
          }
        ]}
      ];
      if (fieldDisplay.showAllDay ?? true) {
        dateTimeTrayConfig.push({data: [{
          label: 'All day',
          key: 'all_day',
          type: 'boolean'
        }]});
      }
      return (dateTimeTrayConfig);
    }
    if (fieldDisplay.type === 'Category') {
      return [{data: [{
        label: fieldDisplay.label,
        key: fieldDisplay.name,
        is_category: true
      }]}];
    }
    if (fieldDisplay.name === 'agency') {
      return [{data: [{
        label: fieldDisplay.label,
        key: 'agency_name',
        type: 'fulltext'
      }]}];
    }
    let type = FIELD_TYPE_TRANSLATIONS[fieldDisplay.type];
    if (type === 'fulltext' && displayConfig.href) {
      type = 'url';
    }
    if (type) {
      return [{data: [{
        label: fieldDisplay.label,
        key: fieldDisplay.name,
        type
      }]}];
    }
    return [];
  };

  const translateSection = sectionConfig => {
    if (sectionConfig.type === 'section') {
      const items = [{data: [{
        label: sectionConfig.label,
        type: 'header'
      }]}];
      sectionConfig.fields?.forEach(fieldRefConfig => {
        items.push(...translateField(fieldRefConfig));
      });
      return items;
    } else if (sectionConfig.type === 'cycles') {
      return [{data: [{type: 'cycles'}]}];
    } else if (sectionConfig.type === 'groups') {
      return [
        {data: [{
          label: 'Groups',
          type: 'header'
        }]},
        {data: [{type: 'groups'}]}
      ];
    } else if (sectionConfig.type === 'locations') {
      return [{data: [{type: 'locations'}]}];
    }
    return translateField(sectionConfig);
  };

  const sections = [];
  entityTrayItems.forEach(configOrName => {
    if (typeof (configOrName) === 'object') {
      sections.push(...translateSection(configOrName));
    } else {
      sections.push(...translateField(configOrName));
    }
  });
  return sections;
}

function translateV2TrayConfig(configV2) {
  const files = configV2?.files;
  const entityTray = configV2?.display?.tray;
  if (!entityTray) {
    return {};
  }
  if (entityTray.title_field?.[0] === '|') {
    return {
      files,
      title: {
        type: 'category',
        source: entityTray.title_field.slice(1)
      }
    };
  }
  if (entityTray.title_field === 'agency') {
    return {
      files,
      title: {
        type: 'agency'
      }
    };
  }
  return {
    files,
    title: {
      type: 'field',
      source: entityTray.title_field || 'name'
    }
  };
}

function translateV2LegacyConfig(configV2, entitySingleOwner = false) {
  const fields = configV2?.fields || {};
  const segmentFields = configV2?.segment_fields || {};
  const {
    title = null,
    type_row: typeRow = null
  } = configV2?.display?.legacy || {};

  const legacy = {};
  if (title) {
    const titleDisplay = resolveFieldRefDisplay(title, fields, segmentFields, entitySingleOwner);
    if (titleDisplay) {
      if (titleDisplay.name === 'agency') {
        legacy.title = {
          label: titleDisplay.label,
          type: 'agency'
        };
      } else if (titleDisplay.type === 'Category') {
        legacy.title = {
          label: titleDisplay.label,
          type: 'category',
          source: titleDisplay.name
        };
      } else {
        legacy.title = {
          label: titleDisplay.label,
          type: 'field',
          source: titleDisplay.name
        };
      }
    }
  }
  if (typeRow) {
    const typeRowDisplay = resolveFieldRefDisplay(typeRow, fields, segmentFields);
    if (typeRowDisplay) {
      if (typeRowDisplay.name === 'agency') {
        legacy['type-row'] = {
          type: 'agency'
        };
      } else if (typeRowDisplay.type === 'Category') {
        legacy['type-row'] = {
          type: 'category',
          source: typeRowDisplay.name
        };
      } else {
        legacy['type-row'] = {
          type: 'field',
          source: typeRowDisplay.name
        };
      }
    }
  }

  return legacy;
}

function flattenDisplayDetails (details) {
  const items = [];
  details.forEach(detail => {
    if (typeof detail === 'object') {
      if (detail.type === 'section') {
        detail.fields.forEach(child => {
          if (typeof child === 'object') {
            if (child.type === 'owners') {
              items.push('owners');
            }
            if (child.type === 'field') {
              items.push(child);
            }
          } else {
            items.push(child);
          }
        });
      }
    } else {
      items.push(detail);
    }
  });
  return items;
}

function translateV2ExportConfig(configV2, entitySingleOwner = false) {
  const fields = configV2?.fields || {};
  const segmentFields = configV2?.segment_fields || {};
  const display = configV2?.display || {};
  let items;
  if (display.export) {
    items = [...display.export];
  } else if (display.dashboard) {
    items = [...display.dashboard];
  } else {
    items = flattenDisplayDetails(display.detail || []);
  }
  const exportConfig = {fields: []};
  items.forEach(exportItem => {
    let displayInfo;
    if (typeof exportItem === 'object') {
      if (exportItem.type === 'segment') {
        exportConfig.fields.push({
          type: 'segment',
          from: exportItem.field,
          to: exportItem.label
        });
        return;
      } else if (exportItem.type === 'field') {
        displayInfo = resolveFieldRefDisplay(exportItem, fields, segmentFields, entitySingleOwner);
      } else {
        const {
          type,
          field,
          label,
          ...params
        } = exportItem;
        exportConfig.fields.push({
          type,
          from: field,
          to: label,
          ...params
        });
        return;
      }
    } else {
      displayInfo = resolveFieldRefDisplay(exportItem, fields, segmentFields, entitySingleOwner);
    }
    if (displayInfo) {
      exportConfig.fields.push({
        type: displayInfo.type === 'Category' ? 'category' : 'field',
        from: displayInfo.name,
        to: displayInfo.label
      });
    }
  });
  return exportConfig;
}

function translateV2DetailsConfig(
  configV2,
  entityLabel,
  categoryValues,
  categoryTypes,
  publicConfig,
  entitySingleOwner = false
) {
  const FIELD_TYPE_TRANSLATIONS = {
    BooleanField: 'boolean',
    CharField: 'string',
    DateField: 'date',
    DateTimeField: 'datetime',
    DecimalField: 'decimal',
    FloatField: 'decimal',
    IntegerField: 'integer',
    TextField: 'string'
  };
  const fields = configV2?.fields || {};
  const segmentFields = configV2?.segment_fields || {};
  const detailItems = publicConfig ? configV2?.display?.public_entry || [] : configV2?.display?.detail || [];

  const sizeTranslations = { tiny: 'col25', small: 'col33', medium: 'col50', large: 'col100' };
  const RESERVED = new Set(['id', 'name', 'agency', 'owners', 'contact', 'date_range', 'generates_overlaps', 'show_public']);

  const detailFieldRefs = {};
  const detailDisplayItems = {};
  for (const detailItem of detailItems) {
    const searchFields = ((typeof detailItem === 'object' && detailItem.type === 'section') ?
      detailItem.fields || [] :
      [detailItem]
    );
    for (const fieldRef of searchFields) {
      if (typeof fieldRef !== 'object') {
        continue;
      }
      if (fieldRef.type === 'field') {
        detailFieldRefs[fieldRef.field] = fieldRef;
      } else {
        detailDisplayItems[fieldRef.type] = fieldRef;
      }
    }
  }

  // ── Build form.columns ────────────────────────────────────────────────────
  const columns = {segments: {}};

  // ID is implicit built-in and cannot be disabled
  columns.id = {
    label: fields?.id?.label || 'ID',
    disabled: false,
    read_only: true,
    type: 'string',
    style: sizeTranslations[fields.id?.size] || 'col25'
  };

  // ID is implicit built-in and cannot be disabled
  columns.name = {
    label: fields.name?.label || 'Name',
    style: sizeTranslations[fields.name?.size] || 'col75',
    disabled: false
  };
  const nameFieldRef = detailFieldRefs.name;
  if (nameFieldRef?.read_only) {
    columns.name.includeInSave = true;
    columns.name.read_only = true;
  }

  if (!fields.agency?.disabled) {
    const agencyConfig = fields.agency || {};
    const agencyFieldRef = detailFieldRefs.agency;
    const agencyCol = {
      label: agencyConfig.label || 'Agency',
      data: 'agency',
      includeInSave: true,
      style: sizeTranslations[fields.agency?.size] || 'col100'
    };
    if (agencyConfig.required) {
      agencyCol.required = true;
    }
    if (agencyConfig.default) {
      agencyCol.defaultId = agencyConfig.default;
    }
    if (agencyFieldRef?.read_only) {
      agencyCol.includeInSave = true;
      agencyCol.disabled = true;
      agencyCol.read_only = true;
    }
    columns.agency = agencyCol;
  }

  if (!fields.owners?.disabled) {
    const ownersConfig = fields.owners || {};
    const ownersFieldRef = detailDisplayItems.owners;
    const ownersCol = {
      data: 'user',
      type: 'field',
      subType: 'array',
      label: ownersConfig.label || (entitySingleOwner ? 'Owner' : 'Owners'),
      style: sizeTranslations[fields.owners?.size] || 'col100'
    };
    ownersCol.defaultLabel = ownersConfig.placeholder || 'Search users';
    if (ownersConfig.default) {
      if (Array.isArray(ownersConfig.default)) {
        ownersCol.defaultIds = ownersConfig.default;
      } else {
        ownersCol.defaultId = ownersConfig.default;
      }
    }
    if (ownersFieldRef?.read_only) {
      ownersCol.includeInSave = true;
      ownersCol.disabled = true;
      ownersCol.read_only = true;
    }
    columns.owners = ownersCol;
  }

  const dateRangeConfig = fields.date_range || {};
  const dateRangeFieldRef = detailDisplayItems.date_range;
  if (!(dateRangeConfig.disable_date && dateRangeConfig.disable_date_time)) {
    const startCol = {
      label: dateRangeConfig.start_label || 'Start Date',
      style: 'startDatetime'
    };
    const endCol = {
      label: dateRangeConfig.end_label || 'End Date',
      style: 'endDatetime'
    };
    if (dateRangeConfig.required_start ?? dateRangeConfig.required) {
      startCol.required = true;
    }
    if (dateRangeConfig.required_end ?? dateRangeConfig.required) {
      endCol.required = true;
    }
    if (dateRangeFieldRef?.read_only) {
      startCol.includeInSave = true;
      startCol.disabled = true;
      startCol.read_only = true;
      endCol.includeInSave = true;
      endCol.disabled = true;
      endCol.read_only = true;
    }
    columns.start_date = startCol;
    columns.end_date = endCol;
    if (dateRangeConfig.disable_date_time) {
      columns.all_day = { label: 'All day', style: 'hidden', default: true };
    } else if (dateRangeConfig.disable_date) {
      columns.all_day = { label: 'All day', style: 'hidden', default: false };
    } else {
      columns.all_day = { label: 'All day', style: 'col100', default: dateRangeFieldRef?.default_all_day ?? false };
      if (dateRangeFieldRef?.read_only) {
        columns.all_day.includeInSave = true;
        columns.all_day.disabled = true;
        columns.all_day.read_only = true;
      }
    }
  }

  if (fields.generates_overlaps && !(fields.generates_overlaps.disabled ?? true)) {
    columns.generates_overlaps = {
      label: fields.generates_overlaps.label || 'Generate Overlap',
      default: fields.generates_overlaps.default ?? true
    };
  }

  if (fields.show_public && !fields.show_public.disabled && fields.show_public.category_restrictions) {
    const spConfig = fields.show_public;
    const restrictions = spConfig.category_restrictions;
    const dataField = restrictions.map(cat => {
      const catName = cat.startsWith('|') ? cat.slice(1) : cat;
      return catName;
    });
    const dataObj = {};
    restrictions.forEach((cat, idx) => {
      dataObj[dataField[idx]] = cat.startsWith('|') ? cat.slice(1) : cat;
    });
    columns.show_public = {
      label: 'Display on Public Viewer',
      style: 'col100',
      componentType: 'toggle',
      data: dataObj,
      dataCheckError: spConfig.category_restriction_message,
      dataCheckErrorStyle: 'toggleError',
      dataCheckValue: false,
      dataField,
      dataExtract: 'is_public',
      dataRelated: dataField,
      default: true
    };
    const showPublicFieldRef = detailFieldRefs?.show_public;
    if (showPublicFieldRef?.read_only) {
      columns.show_public.includeInSave = true;
      columns.show_public.disabled = true;
      columns.show_public.read_only = true;
    }
  }

  if (fields.contact && !fields.contact.disabled) {
    const contactFieldRef = detailDisplayItems?.contact;
    const contactCol = {
      label: detailDisplayItems?.contact?.label || fields.contact?.label || 'Contact',
      children: {
        email: {
          required: fields.contact.required,
          style: 'col50'
        },
        phone: {
          required: fields.contact.required && (fields.contact.require_phone ?? true),
          style: 'col50'
        }
      }
    };
    if (!fields.contact.disable_name) {
      contactCol.children.name = {
        required: fields.contact.required && (fields.contact.require_name ?? true)
      };
    }
    if (contactFieldRef?.read_only) {
      contactCol.children.email.includeInSave = true;
      contactCol.children.email.disabled = true;
      contactCol.children.email.read_only = true;
      contactCol.children.phone.includeInSave = true;
      contactCol.children.phone.disabled = true;
      contactCol.children.phone.read_only = true;
      if (contactCol.children.name) {
        contactCol.children.name.includeInSave = true;
        contactCol.children.name.disabled = true;
        contactCol.children.name.read_only = true;
      }
    }
    columns.contact = contactCol;
  }

  // Category fields
  Object.entries(fields).forEach(([key, fieldConfig]) => {
    if (!key.startsWith('|') || fieldConfig.disabled) {
      return;
    }
    const col = {
      label: fieldConfig.label,
      style: sizeTranslations[fieldConfig.size] || 'col50',
      data: 'map_category',
      type: 'field'
    };
    if (fieldConfig.required) {
      col.required = true;
    }
    if (fieldConfig.placeholder) {
      col.placeholder = fieldConfig.placeholder;
    }
    if (fieldConfig.multiple) {
      col.multiple = fieldConfig.multiple;
    }
    const detailFieldRef = detailFieldRefs[key] || null;
    const default_value = (detailFieldRef?.default || fieldConfig.default) ?? null;
    if (default_value !== null) {
      const categoryType = (categoryTypes || []).find(({name}) => name === fieldConfig.name);
      const categoryValue = categoryValues[default_value];
      if (categoryType && categoryValue && categoryValue.type === categoryType.id) {
        col.defaultName = categoryValue.name;
      }
    }
    if (detailFieldRef?.required) {
      // Upgrade required
      col.required = true;
    }
    if (detailFieldRef?.read_only) {
      col.includeInSave = true;
      col.disabled = true;
      col.read_only = true;
    }
    if (detailFieldRef?.radio) {
      col.componentType = 'radio';
    }
    if (detailFieldRef?.header) {
      col.title = {type: 'subheader', label: detailFieldRef.header};
    }
    columns[key] = col;
  });

  // Dynamic fields
  Object.entries(fields).forEach(([key, fieldConfig]) => {
    if (key.startsWith('|') || RESERVED.has(key) || fieldConfig.disabled) {
      return;
    }
    const col = {
      type: FIELD_TYPE_TRANSLATIONS[fieldConfig.type],
      style: sizeTranslations[fieldConfig.size] || 'col50'
    };
    if (fieldConfig.label) {
      col.label = fieldConfig.label;
    }
    if (fieldConfig.required) {
      col.required = true;
    }
    if (typeof fieldConfig.default_display !== 'undefined') {
      col.default_internal = fieldConfig.default_display;
    }
    if (fieldConfig?.associated_end) {
      col.associatedField = fieldConfig.associated_end;
      col.isFirstInAssociation = true;
    }
    if (fieldConfig?.associated_start) {
      col.associatedField = fieldConfig.associated_start;
      col.isFirstInAssociation = false;
    }
    if (fieldConfig.type === 'BooleanField' && fieldConfig.null_label) {
      col.defaultNullLabel = fieldConfig.null_label;
    }
    let detailFieldRef = detailFieldRefs[key] || null;
    for (const detailItem of detailItems) {
      const searchFields = ((typeof detailItem === 'object' && detailItem.type === 'section') ?
        detailItem.fields || [] :
        [detailItem]
      );
      for (const fieldRef of searchFields) {
        if (typeof fieldRef === 'object' && fieldRef.type === 'field' && fieldRef.field === key) {
          detailFieldRef = fieldRef;
          break;
        }
      }
      if (detailFieldRef) {
        break;
      }
    }
    if (detailFieldRef?.required) {
      // Upgrade required
      col.required = true;
    }
    if (detailFieldRef?.read_only) {
      col.includeInSave = true;
      col.disabled = true;
      col.read_only = true;
    }
    if (col.type === 'boolean' && detailFieldRef?.radio) {
      col.componentType = 'radio';
    }
    if (col.type === 'boolean' && detailFieldRef?.dropdown) {
      col.componentType = 'dropdown';
      // Booleans normally default to false, but dropdown booleans default to null when not required
      if (!col.required) {
        col.default = null;
      }
    }
    if (col.type === 'datetime' && detailFieldRef?.multiline) {
      col.detailFullDateTime = true;
      col.style = 'col100';
    }
    if (detailFieldRef?.header) {
      col.title = {type: 'subheader', label: detailFieldRef.header};
    }
    columns[key] = col;
  });

  // ── Build tabs ────────────────────────────────────────────────────────────
  const tabs = [];
  const makeLabelId = label => label.toLowerCase().replace(/\s+/g, '-');

  const hasRequired = fieldList => fieldList.some(fieldRef => {
    if (typeof fieldRef === 'object') {
      if (fieldRef.required) {
        // If required override set return true
        return true;
      }
      if (fieldRef.type === 'owners') {
        return !!(fields.owners?.required);
      }
      if (fieldRef.type === 'contact') {
        return !!(fields.contact?.required);
      }
    }
    const name = typeof fieldRef === 'string' ? fieldRef : fieldRef.field;
    if (name) {
      if (name === 'name') {
        return true;
      }
      if (name === 'date_range') {
        return (
          !!(fields.date_range?.required) ||
          !!(fields.date_range?.required_start) ||
          !!(fields.date_range?.required_end)
        );
      }
      if (name === 'agency') {
        return !!(fields.agency?.required);
      }
      if (name === 'owners') {
        return !!(fields.owners?.required);
      }
      if (name === 'contact') {
        return !!(fields.contact?.required);
      }
      return !!(fields[name]?.required);
    }
    return false;
  });

  const buildTabFields = sectionFields => {
    const tabFields = [];
    const extra = {};
    let dateRangeFields = null;
    sectionFields.forEach(fieldRef => {
      const name = typeof fieldRef === 'string' ? fieldRef : fieldRef.field;
      if (
        fieldRef.type === 'date_range' ||
        name === 'date_range'
      ) {
        tabFields.push('_date_range', '_date_warning');
        const dr = fields.date_range || {};
        if (!(dr.disable_date && dr.disable_date_time)) {
          dateRangeFields = ['start_date', 'end_date'];
          if (!dr.disable_date_time && !dr.disable_date) {
            dateRangeFields.push('all_day');
          }
        }
        if (fieldRef.multiline) {
          extra.date_range_full = true;
        }
        if (fieldRef.split_range) {
          extra.date_range_split = true;
        }
        if (fieldRef.start_header) {
          extra.date_start_header = fieldRef.start_header;
        }
        if (fieldRef.end_header) {
          extra.date_end_header = fieldRef.end_header;
        }
      } else if (
        fieldRef.type === 'owners' ||
        name === 'owners'
      ) {
        tabFields.push('owners');
      } else if (fieldRef.type === 'contact' || name === 'contact') {
        tabFields.push('contact');
      } else if (fieldRef.type === 'text' && fieldRef.style === 'alert') {
        tabFields.push('_info_message');
        extra._info_message = fieldRef.text;
      } else if (fieldRef.type === 'text') {
        tabFields.push('_textarea');
        extra._textarea_data = fieldRef.text;
      } else if (name) {
        const columnConfig = columns[name];
        if (columnConfig) {
          tabFields.push(name);
        }
      }
    });
    return { tabFields, dateRangeFields, extra };
  };

  detailItems.forEach(item => {
    const itemType = typeof item === 'string' ? null : item.type;

    if (itemType === 'section') {
      const { tabFields, dateRangeFields, extra } = buildTabFields(item.fields || []);
      const tab = {
        id: makeLabelId(item.label || []),
        label: item.label,
        ...(hasRequired(item.fields || []) ? { extra: '*Required' } : {}),
        fields: tabFields,
        ...extra
      };
      if (dateRangeFields) {
        tab.date_range_fields = dateRangeFields;
      }
      tabs.push(tab);
    } else if (itemType === 'locations') {
      columns.segments = {};
      if (item.read_only) {
        columns.segments.read_only = true;
        columns.segments.disabled = true;
      }
      const defaultFields = [];
      const v2ShapeType = 'shape_type';
      const legacyShapeType = 'shape-type';
      const implicitSegFields = ['addresses', 'snap_n_alley'];
      const optionalSegFields = ['impact', 'status', 'title', 'description', 'schedule'];
      const defaultDisabledStatuses = new Set(['status']);
      optionalSegFields.forEach(segField => {
        const fieldConfig = segmentFields[segField];
        const defaultDisabled = defaultDisabledStatuses.has(segField);
        const enabled = defaultDisabled ? !(fieldConfig?.disabled ?? true) : !fieldConfig?.disabled;
        if (enabled) {
          defaultFields.push(segField);
        }
        if (fieldConfig) {
          columns.segments[segField] = fieldConfig;
        } else {
          columns.segments[segField] = {
            disabled: defaultDisabled,
            required: false
          };
        }
      });
      const builtinKeys = new Set([v2ShapeType, ...implicitSegFields, ...optionalSegFields]);
      Object.keys(segmentFields).forEach(key => {
        if (!builtinKeys.has(key) && !segmentFields[key]?.disabled) {
          defaultFields.push(key);
        }
        columns.segments[key] = segmentFields[key];
      });
      let segFieldList;
      if (configV2.display?.segments_details) {
        segFieldList = [
          legacyShapeType,
          ...implicitSegFields,
          ...configV2.display.segments_details.filter(fieldName => columns.segments[fieldName])
        ];
      } else {
        segFieldList = [legacyShapeType, ...implicitSegFields, ...defaultFields];
      }
      const shapeType = segmentFields.shape_type || {};
      const segTypes = ['point', 'linestring', 'polygon'].filter(shapeKey => shapeType[shapeKey] !== false);
      const locationTab = {
        id: 'locations',
        label: 'Location',
        extra: (configV2.segment_required ?? true) ? 'Required' : '',
        fields: ['segments'],
        segment_fields: segFieldList,
        segment_types: segTypes,
        type: 'segment'
      };
      tabs.push(locationTab);
    } else if (
      itemType === 'owners' ||
      (itemType === 'field' && item.field === 'owners') ||
      (typeof item === 'string' && item === 'owners')
    ) {
      const ownerConfig = fields.owners || {};
      const labelOverride = typeof item === 'object' ? item.label : null;
      const requiredOverride = typeof item === 'object' ? item.required : null;
      tabs.push({
        id: 'owners',
        label: labelOverride || ownerConfig.label || (entitySingleOwner ? 'Owner' : 'Owners'),
        ...((requiredOverride || ownerConfig.required) ? { extra: 'Required' } : {}),
        fields: ['owners']
      });
    } else if (itemType === 'review') {
      tabs.push({
        id: 'review',
        label: item?.label || 'Review'
      });
    } else if (itemType === 'attachment') {
      const attachmentTab = {
        id: 'documents',
        label: item?.label || 'Attachments',
        hint: item?.hint || 'Add Files'
      };
      if (item.type) {
        attachmentTab.type = item.type;
      }
      tabs.push(attachmentTab);
    } else if (itemType === 'field' || typeof item === 'string') {
      const fieldName = typeof item === 'string' ? item : item.field;
      const labelOverride = typeof item === 'object' ? item.label : null;
      const columnConfig = columns[fieldName];
      if (columnConfig) {
        const label = labelOverride || columnConfig.label || fieldName;
        tabs.push({
          id: makeLabelId(label),
          label,
          fields: [fieldName]
        });
      }
    }
    // cycles, groups: no direct legacy equivalent — skip
  });

  if (publicConfig) {
    tabs.forEach((section, index) => {
      section.step = index;
    });
  }

  return {
    form: {
      title: { label: entityLabel || '' },
      columns
    },
    tabs
  };
}

function translateV2DashboardConfig(
  configV2,
  entitySingleOwner = false,
  entityTypes = [],
  displayConfig = null,
  overlapDashboard = false
) {
  const fields = configV2?.fields || {};
  const segmentFields = configV2?.segment_fields || {};
  const display = configV2?.display || {};
  let items;
  if (displayConfig) {
    items = [...displayConfig];
  } else if (display.dashboard) {
    items = [...display.dashboard];
  } else {
    items = flattenDisplayDetails(display.detail || []);
  }
  const dashboardFields = [];
  items.forEach(dashboardItem => {
    let displayInfo;
    if (typeof dashboardItem === 'object') {
      if (dashboardItem.type === 'overlap_status') {
        dashboardFields.push({
          label: 'Status',
          key: 'status',
          ordering: 'conflict_status',
          initOrder: 'asc',
          defaultOrder: true,
          width: Number(dashboardItem.size_rem) || 7
        });
        return;
      }
      if (dashboardItem.type === 'overlap_agencies') {
        dashboardFields.push({
          label: 'Agencies in overlap',
          key: 'agencies',
          width: Number(dashboardItem.size_rem) || 11.5
        });
        return;
      }
      if (dashboardItem.type === 'overlap_metrics') {
        if (dashboardItem.show_conflicts_metric) {
          dashboardFields.push({
            label: 'Conf.',
            key: 'conflict_count',
            ordering: 'cache__conflict_count',
            initOrder: 'desc',
            styleName: 'right',
            width: Number(dashboardItem.size_rem) || 6.5
          });
        }
        if (dashboardItem.show_opportunities_metric) {
          dashboardFields.push({
            label: 'Opps.',
            key: 'opportunity_count',
            ordering: 'cache__opportunity_count',
            initOrder: 'desc',
            styleName: 'right',
            width: Number(dashboardItem.size_rem) || 6.5
          });
        }
        dashboardItem.entity_metrics?.forEach(entityName => {
          const entityInfo = entityTypes.find(info => info.name === entityName);
          if (entityInfo) {
            dashboardFields.push({
              label: entityInfo.label,
              key: `${entityName}_count`,
              ordering: `cache__entities_count__${entityName}`,
              initOrder: 'desc',
              styleName: 'right',
              width: Number(dashboardItem.size_rem) || 6.5
            });
          }
        });
        return;
      }
      if (dashboardItem.type === 'segment') {
        dashboardFields.push({
          label: dashboardItem.label || 'Locations',
          key: 'on_street',
          width: Number(dashboardItem.size_rem) || 12.5
        });
        return;
      } else if (
        dashboardItem.type === 'owners' ||
        dashboardItem.field === 'owners'
      ) {
        let field;
        if (overlapDashboard) {
          field = {
            label: dashboardItem.label || fields.owners?.label || (entitySingleOwner ? 'Owner' : 'Owners'),
            key: 'owners',
            width: Number(dashboardItem.size_rem) || 11
          };
        } else {
          field = {
            label: dashboardItem.label || fields.owners?.label || (entitySingleOwner ? 'Owner' : 'Owners'),
            key: 'owner_list',
            width: Number(dashboardItem.size_rem) || 11
          };
        }
        if (!(dashboardItem.disable_order)) {
          field.ordering = 'owners__name';
          field.initOrder = dashboardItem.initial_order || 'asc';
        }
        if (dashboardItem.default_order) {
          field.defaultOrder = true;
        }
        dashboardFields.push(field);
      } else if (dashboardItem.type === 'groups') {
        const field = {
          label: dashboardItem.label || 'Groups',
          key: 'groups',
          width: Number(dashboardItem.size_rem) || 12
        };
        dashboardFields.push(field);
      } else if (dashboardItem.type === 'modified') {
        const field = {
          label: dashboardItem.label || 'Last modified',
          key: overlapDashboard ? 'last_modified' : 'modified',
          ordering: overlapDashboard ? 'cache__overlap_last_modified' : 'modified',
          initOrder: dashboardItem.initial_order || 'desc',
          styleName: 'uppercase',
          width: Number(dashboardItem.size_rem) || 9.5
        };
        if (dashboardItem.default_order) {
          field.defaultOrder = true;
        }
        dashboardFields.push(field);
      } else if (
        dashboardItem.type === 'date_range' ||
        (dashboardItem.type === 'field' && dashboardItem.field === 'date_range')
      ) {
        /* eslint-disable max-depth */
        if (dashboardItem.show_time) {
          if (dashboardItem.start ?? true) {
            dashboardFields.push({
              label: dashboardItem.start_label || 'Start time',
              key: 'start_time',
              styleName: 'uppercase',
              width: Number(dashboardItem.size_rem) || 8.5
            });
          }
          if (dashboardItem.end ?? true) {
            dashboardFields.push({
              label: dashboardItem.end_label || 'End time',
              key: 'end_time',
              styleName: 'uppercase',
              width: Number(dashboardItem.size_rem) || 8.5
            });
          }
          return;
        }
        if (dashboardItem.start ?? true) {
          const start_field = {
            label: dashboardItem.start_label || fields.date_range?.start_label || 'Start date',
            key: 'start_date',
            ordering: 'start_date',
            initOrder: dashboardItem.start_initial_order || 'asc',
            styleName: 'uppercase',
            width: Number(dashboardItem.size_rem) || 8.5
          };
          if (dashboardItem.start_default_order) {
            start_field.defaultOrder = true;
          }
          dashboardFields.push(start_field);
        }
        if (dashboardItem.end ?? true) {
          const end_field = {
            label: dashboardItem.end_label || fields.date_range?.end_label || 'End date',
            key: 'end_date',
            ordering: 'end_date',
            initOrder: dashboardItem.end_initial_order || 'asc',
            styleName: 'uppercase',
            width: Number(dashboardItem.size_rem) || 8.5
          };
          if (dashboardItem.end_default_order) {
            end_field.defaultOrder = true;
          }
          dashboardFields.push(end_field);
          return;
        }
        /* eslint-enable max-depth */
      } else if (dashboardItem.type === 'field') {
        displayInfo = resolveFieldRefDisplay(dashboardItem, fields, segmentFields, entitySingleOwner);
        if (dashboardItem.highlight) {
          displayInfo.highlight = true;
        }
      }
    } else {
      displayInfo = resolveFieldRefDisplay(dashboardItem, fields, segmentFields, entitySingleOwner);
    }
    if (displayInfo) {
      const category = displayInfo.field[0] === '|';
      const field = {
        label: displayInfo.label,
        key: displayInfo.name,
        width: displayInfo.sizeRem
      };
      // Some fields can not be ordered by the backend. Ignore ordering params for such fields.
      const canNotBeOrdered = (
        displayInfo.multiple ?? false
      );
      if (!displayInfo.disableOrder && !canNotBeOrdered) {
        field.initOrder = displayInfo.initialOrder;
        if (category) {
          field.ordering = `category|${displayInfo.name}`;
        } else if (displayInfo.field === 'agency') {
          field.ordering = 'agency__name';
        } else if (displayInfo.field === 'owners') {
          field.ordering = 'owners__name';
        } else {
          field.ordering = displayInfo.name;
        }
        if (displayInfo.defaultOrder) {
          field.defaultOrder = true;
        }
      }
      if (displayInfo.highlight && displayInfo.field !== 'id') {
        field.styleName = 'strong';
      }
      if (displayInfo.field === 'id') {
        field.styleName = 'right';
      }
      dashboardFields.push(field);
    }
  });
  return dashboardFields;
}

function resolveCategoryIconType(categoryName, categoryValues, categoryTypes) {
  if (categoryName === 'priority') {
    return 'priority';
  }
  const categoryType = (categoryTypes || []).find(ct => ct?.name === categoryName);
  if (!categoryType) {
    return null;
  }
  const hasIcon = Object.values(categoryValues || {}).some(
    categoryValue => categoryValue.type === categoryType.id && categoryValue.attrs?.icon_name
  );
  return hasIcon ? 'category' : null;
}

function translateV2MapFilterSections(entityType, categoryValues, categoryTypes, entitySingleOwner = false) {
  const configV2 = entityType.config;
  const filters = configV2?.filters?.map || [];
  const fields = configV2?.fields || {};

  const sections = [];
  filters.forEach(mapFilterInfo => {
    const isObject = typeof mapFilterInfo === 'object';
    if (isObject && mapFilterInfo.type === 'map_agency_combo') {
      const agencyTypeSection = {
        type: 'data-type-group',
        data_type: 'agency_type',
        filter_name: 'agency_type',
        title: 'Select agency types',
        radio: true,
        radioType: 'agencyType',
        icon_type: 'agency'
      };
      if (mapFilterInfo.alwaysEnabled) {
        agencyTypeSection.negateIfEmpty = true;
      }
      sections.push(agencyTypeSection);
      sections.push({
        type: 'data-type-search',
        data_type: 'agency',
        filter_name: 'agency',
        placeholder: 'Search agencies',
        radio: true,
        radioType: 'agency',
        title: 'Select specific agencies'
      });
    } else if (isObject && mapFilterInfo.type === 'agency_type') {
      const agencyTypeSection = {
        type: 'data-type-group',
        data_type: 'agency_type',
        filter_name: 'agency_type',
        title: 'Select agency types',
        icon_type: 'agency'
      };
      if (mapFilterInfo.alwaysEnabled) {
        agencyTypeSection.negateIfEmpty = true;
      }
      sections.push(agencyTypeSection);
    } else if (isObject && mapFilterInfo.type === 'agency') {
      sections.push({
        type: 'data-type-search',
        data_type: 'agency',
        filter_name: 'agency',
        placeholder: 'Search agencies',
        title: 'Agencies'
      });
    } else if (isObject && mapFilterInfo.type === 'map_overlap_highlights') {
      sections.push({
        type: 'data-type-overlap'
      });
    } else if (isObject && mapFilterInfo.type === 'map_filter_separator') {
      sections.push({
        type: 'separator'
      });
    } else if (isObject && (mapFilterInfo.type === 'owners')) {
      sections.push({
        type: 'data-type-search',
        data_type: 'user',
        filter_name: 'owners',
        placeholder: mapFilterInfo?.placeholder || fields?.owners?.placeholder || 'Search users',
        title: mapFilterInfo?.label || fields?.owners?.label || (entitySingleOwner ? 'Owner' : 'Owners')
      });
    } else {
      let filterData;
      let iconType;
      const filterConfig = resolveFieldFilter(mapFilterInfo, fields);
      if (!filterConfig) {
        return;
      }
      if (filterConfig.type === 'select') {
        if (filterConfig.name === 'user') {
          filterData = {
            type: 'data-type-search',
            data_type: 'user',
            filter_name: 'owners',
            title: filterConfig.label || 'Users',
            placeholder: filterConfig.placeholder || 'Search users'
          };
        } else if (filterConfig.name === 'agency') {
          filterData = {
            type: 'data-type-search',
            data_type: 'agency',
            filter_name: 'agency',
            title: filterConfig.label || 'Select specific agencies',
            placeholder: filterConfig.placeholder || 'Search agencies'
          };
        } else {
          filterData = {
            type: 'data-type-group',
            data_type: 'map_category',
            category: filterConfig.name,
            filter_name: 'category',
            title: filterConfig.label || filterConfig.name
          };
          iconType = resolveCategoryIconType(filterData.category, categoryValues, categoryTypes);
        }
        if (mapFilterInfo.alwaysEnabled) {
          filterData.negateIfEmpty = true;
        }
        if (iconType) {
          filterData.icon_type = iconType;
        }
        sections.push(filterData);
      }
      if (filterConfig.type === 'search') {
        sections.push({
          type: 'data-type-search',
          data_type: filterConfig.name,
          filter_name: filterConfig.name,
          placeholder: filterConfig.placeholder || 'Search',
          title: filterConfig.label || filterConfig.name
        });
      }
      if (filterConfig.type === 'attribute_search') {
        sections.push({
          type: 'entity-attr-search',
          data_type: entityType.name,
          filter_name: filterConfig.name,
          field: filterConfig.name,
          placeholder: filterConfig.placeholder || 'Search',
          title: filterConfig.label || filterConfig.name
        });
      }
      if (filterConfig.type === 'boolean') {
        sections.push({
          type: 'data-type-group',
          filter_name: filterConfig.name,
          field: filterConfig.name,
          title: filterConfig.label,
          values: filterConfig.values ?? [{
            id: 1,
            name: 'Funded'
          }]
        });
      }
    }
  });
  return sections;
}

function translateV2DashboardFilterConfig(
  configV2,
  filterConfig,
  categoryValues,
  categoryTypes,
  entitySingleOwner = false
) {
  const fields = configV2?.fields || {};
  const segmentFields = configV2?.segment_fields || {};
  const items = [];
  filterConfig.forEach(config => {
    let fieldName;
    if (typeof config === 'object') {
      if (config.type === 'agency_type') {
        items.push({
          name: 'agency_type',
          params: ['agency__type__in'],
          type: 'agency-type'
        });
        return;
      } else if (config.type === 'agency') {
        items.push({
          name: 'agency',
          params: ['agency__in'],
          type: 'agency'
        });
        return;
      } else if (config.type === 'owners') {
        const displayInfo = resolveFieldRefDisplay('owners', fields, segmentFields, entitySingleOwner);
        items.push({
          label: config.label ?? displayInfo.label,
          name: 'owners',
          params: ['owners__in'],
          type: 'user'
        });
        return;
      } else if (config.type === 'date_range') {
        items.push({
          name: 'date_range',
          params: [
            'end_date__gte',
            'start_date__lte',
            'end_date__lte',
            'start_date__gte'
          ],
          type: 'date'
        });
        return;
      } else if (config.type === 'date_range_time') {
        if (config.start ?? true) {
          items.push({
            name: 'date_range_time_start',
            label: 'Start Time',
            params: [
              'start_time__gte',
              'start_time__lte'
            ],
            type: 'time'
          });
        }
        if (config.end ?? true) {
          items.push({
            name: 'date_range_time_end',
            label: 'End Time',
            params: [
              'end_time__gte',
              'end_time__lte'
            ],
            type: 'time'
          });
        }
        return;
      } else if (config.type === 'overlap_status') {
        items.push({
          items: [
            {
              id: 1,
              name: 'Open'
            },
            {
              id: 2,
              name: 'Pending'
            },
            {
              id: 3,
              name: 'Resolved'
            },
            {
              id: 100,
              name: 'Opportunity'
            }
          ],
          label: config.label ?? 'Status',
          name: 'overlap_status',
          params: ['conflict_status__in'],
          type: 'field'
        });
        return;
      } else if (config.type === 'field') {
        fieldName = config.field;
      }
    } else {
      fieldName = config;
    }
    if (fieldName) {
      const displayInfo = resolveFieldRefDisplay(fieldName, fields, segmentFields, entitySingleOwner);
      if (!displayInfo) {
        return;
      }
      if (displayInfo.field[0] === '|') {
        // Category filters
        const filterItem = {
          name: displayInfo.name,
          params: ['categories__in'],
          type: 'category'
        };
        if (resolveCategoryIconType(filterItem.name, categoryValues, categoryTypes) === 'category') {
          filterItem.showIcon = true;
        }
        items.push(filterItem);
        return;
      }
      if (displayInfo.type === 'BooleanField') {
        items.push({
          name: displayInfo.name,
          label: config.label ?? displayInfo.label,
          params: [`attrs__${displayInfo.name}`],
          onLabel: displayInfo.true_label || 'True',
          offLabel: displayInfo.false_label || 'false',
          type: 'checkbox'
        });
        return;
      }
      if (displayInfo.type === 'DateField' || displayInfo.type === 'DateTimeField') {
        items.push({
          label: config.label ?? displayInfo.label,
          name: displayInfo.name,
          params: [`${displayInfo.name}__gte`, `${displayInfo.name}__lte`],
          type: 'past-date'
        });
        return;
      }
      // Nothing else is supported by legacy dashboard filters.
    }
  });
  return items;
}

function translateFilterDefaults (
  configV2, filterConfig, agencyTypes, categoryValues, categoryTypes, globalFilters
) {
  const fields = configV2?.fields || {};
  const segmentFields = configV2?.segment_fields || {};
  const defaults = {};
  if (!filterConfig) {
    return defaults;
  }

  function resolveAgencyTypeDefault(explicitDefault) {
    if (explicitDefault) {
      return explicitDefault;
    }
    if (!agencyTypes) {
      return null;
    }
    const ids = Object.values(agencyTypes)
      .filter(typeData => typeData.show_on_map)
      .map(typeData => String(typeData.id))
      .join(',');
    return ids || null;
  }

  function resolveCategoryDefault(categoryName, explicitDefault) {
    if (explicitDefault) {
      return explicitDefault;
    }
    if (!categoryValues || !categoryTypes) {
      return null;
    }
    const category = categoryTypes.find(({name}) => name === categoryName);
    if (!category) {
      return null;
    }
    const ids = Object.values(categoryValues)
      .filter(({active, type, id}) => active && type === category.id && typeof id === 'number')
      .map(({id}) => id)
      .join(',');
    return ids ? `${categoryName},${ids}` : null;
  }

  filterConfig.forEach(config => {
    if (typeof config === 'object') {
      if (config.type === 'map_agency_combo') {
        const value = resolveAgencyTypeDefault(config.default);
        if (value) {
          defaults.agency__type__in = value;
        }
        return;
      } else if (config.type === 'agency_type') {
        const value = resolveAgencyTypeDefault(config.default);
        if (value) {
          defaults.agency__type__in = value;
        }
        return;
      } else if (config.type === 'agency') {
        if (config.default) {
          defaults.agency__in = config.default;
        }
        return;
      } else if (config.type === 'owners') {
        return;
      } else if (config.type === 'date_range') {
        if (config.default === 'custom') {
          defaults.end_date__gte = globalFilters.end_date__gte || null;
          defaults.start_date__lte = globalFilters.start_date__lte || null;
        }
        return;
      } else if (config.type === 'overlap_status') {
        if (config.default) {
          defaults.conflict_status__in = config.default;
        }
        return;
      } else if (config.type === 'field') {
        const displayInfo = resolveFieldRefDisplay(config.field, fields, segmentFields);
        if (displayInfo?.field[0] !== '|') {
          return;
        }
        const categoryName = displayInfo.field.slice(1);
        const value = resolveCategoryDefault(categoryName, config.default);
        if (value) {
          defaults[`categories__in${displayInfo.field}`] = value;
        }
      }
    }
  });
  return defaults;
}

/**
 * Merges v2 EntityType config data into the stripped v1 config,
 * restoring every path removed by stripEntityConfig().
 *
 * @param {object} strippedConfig - v1 config after stripEntityConfig()
 * @param {Array}  v2EntityData   - response from api/config_v2/entity_type
 * @param {Array}  agencyTypes    - list of agency types
 * @param {Array}  categoryValues - list of category values
 *   Each element: { id, name, label, display_name, color, icon, config: EntityTypeConfig }
 * @returns {object} merged config
 */
export function applyEntityConfig(strippedConfig, v2EntityData, agencyTypes, categoryValues, publicConfig = false) {
  const newConfig = JSON.parse(JSON.stringify(strippedConfig));
  const globalFilters = {...strippedConfig.filters?.global};
  const entityNames = v2EntityData.map(entityType => entityType.name);

  // ── Whole-section restorations ────────────────────────────────────────────

  // entity_types / entities
  newConfig.entity_types = v2EntityData.map(entityType => ({
    id: entityType.id,
    name: entityType.name,
    label: entityType.label,
    display_name: entityType.display_name,
    max_owners: entityType.max_owners,
    overlaps: entityType.overlaps,
    config: {
      ...entityType.config?.legacy,
      attributes: {
        ...entityType.config?.legacy?.attributes,
        disableUiAdd: entityType.config?.display?.hide_add || false
      },
      segments: {
        required: entityType.config?.segment_required ?? true
      },
      frontend: {
        ...entityType.config?.legacy?.frontend,
        card: {
          ...entityType.config?.legacy?.frontend?.card,
          chips: translateV2ChipsConfig(entityType.config)
        }
      },
      categories: (Object.entries(entityType.config?.fields || {})
        .filter(([key]) => key[0] === '|')
        .map(([, {name}]) => name)
      ),
      fields: translateV2Fields(entityType.config)
    }
  }));
  newConfig.entities = entityNames;

  // // dataTypes.entity — new primary path used by getDataTypeConfig() selector
  // newConfig.dataTypes = newConfig.dataTypes || {};
  // newConfig.dataTypes.entity = {};
  // v2EntityData.forEach(entityType => {
  //   newConfig.dataTypes.entity[entityType.name] = entityType;
  // });

  // attachments → EntityTypeConfig.files
  const attachmentEnabled = (
    v2EntityData.filter(entityType => entityType.config?.files).map(entityType => entityType.name)
  );
  newConfig.attachments = { enabled: attachmentEnabled };

  // schedule → EntityTypeConfig.segment_fields.schedule
  const scheduleEnabled = v2EntityData
    .filter(entityType => !entityType.config?.segment_fields?.schedule?.disabled)
    .map(entityType => entityType.name);
  newConfig.schedule = { enabled: scheduleEnabled };

  // detailTrayConfig → EntityTypeConfig.display.detail
  const detailTrayConfig = newConfig.detailTrayConfig || {};
  v2EntityData.forEach(entityType => {
    detailTrayConfig[entityType.name] = translateV2DetailTrayConfig(entityType.config, entityType.max_owners === 1);
  });
  if (Object.keys(detailTrayConfig).length > 0) {
    newConfig.detailTrayConfig = detailTrayConfig;
  }

  // tray → EntityTypeConfig.display.tray
  const tray = {};
  v2EntityData.forEach(entityType => {
    const tryConfig = translateV2TrayConfig(entityType.config);
    if (tryConfig) {
      tray[entityType.name] = tryConfig;
    }
  });
  if (Object.keys(tray).length > 0) {
    newConfig.tray = tray;
  }

  // list-item → EntityTypeConfig.display.legacy
  const listItem = {};
  v2EntityData.forEach(entityType => {
    if (entityType.config?.display?.legacy) {
      listItem[entityType.name] = translateV2LegacyConfig(
        entityType.config,
        entityType.max_owners === 1
      );
    }
  });
  if (Object.keys(listItem).length > 0) {
    newConfig['list-item'] = listItem;
  }

  // export → EntityTypeConfig.display.export
  const exportConfig = {};
  v2EntityData.forEach(entityType => {
    if (entityType.config) {
      exportConfig[entityType.name] = translateV2ExportConfig(
        entityType.config,
        entityType.max_owners === 1
      );
    }
  });
  if (Object.keys(exportConfig).length > 0) {
    newConfig.export = exportConfig;
  }

  // ── Partial-section restorations ─────────────────────────────────────────

  // map.legend.items → CategoryFieldConfig entries with map_icon === true
  // map.legend.items → CategoryFieldConfig entries with map_icon === true
  if (newConfig.map?.legend) {
    const legendItems = [];
    v2EntityData.forEach(entityType => {
      let legendAdded = false;
      if (
        !entityType.config?.fields?.agency?.disabled && (
          !entityType.config?.fields?.agency ||
          entityType.config?.fields?.agency.map_icon
        )
      ) {
        legendItems.push({
          id: entityType.name,
          title: entityType.config?.filters?.map_label || entityType.label,
          subtitle: 'BY AGENCY TYPE',
          type: 'agency',
          entities: [entityType.name]
        });
        legendAdded = true;
      }
      Object.entries(entityType.config?.fields ?? {}).forEach(([key, fieldConfig]) => {
        if (key.startsWith('|') && fieldConfig.map_icon) {
          const categoryName = key.slice(1);
          const category = (strippedConfig.category_types || []).find(categoryData => (
            categoryData?.name === categoryName
          ));
          if (category) {
            legendItems.push({
              id: entityType.name,
              title: entityType.config?.filters?.map_label || entityType.label,
              subtitle: (
                fieldConfig.map_icon_legend_label ||
                `BY ${category.label}`.toUpperCase()
              ),
              type: 'entity-category',
              'category-type': category.id,
              entities: [entityType.name]
            });
            legendAdded = true;
          }
        }
      });
      if (!legendAdded) {
        legendItems.push({
          id: entityType.name,
          title: entityType.config?.filters?.map_label || entityType.label,
          type: 'entity',
          entities: [entityType.name]
        });
      }
    });
    // Append existing items to end
    newConfig.map.legend.items = [...legendItems, ...newConfig.map.legend.items];
  }

  // categories.entities → |category keys in EntityTypeConfig.fields
  // also data for agency_type
  const categoriesEntities = {};
  v2EntityData.forEach(entityType => {
    categoriesEntities[entityType.name] = {};
    
    if (!entityType.config?.fields?.agency?.disabled) {
      categoriesEntities[entityType.name].agency__type__in = (Object.values(agencyTypes)
        .filter(typeData => typeData.show_on_map)
        .map(typeData => `${typeData.id}`)
      ).join(',');
    }
    Object.entries(entityType.config?.fields ?? {}).forEach(([key, v2Field]) => {
      if (!key.startsWith('|') || v2Field.disabled) {
        return;
      }
      const categoryName = key.slice(1);
      const category = (strippedConfig.category_types || []).find(categoryData => (
        categoryData?.name === categoryName
      ));
      if (category) {
        const categoryId = category.id;
        const categoryIds = (Object.values(categoryValues)
          .filter(({active, type, id}) => (
            active && type === categoryId && typeof id === 'number'
          ))
          .map(({id}) => id)
          .join(',')
        );
        categoriesEntities[entityType.name][`categories__in|${categoryName}`] = `${categoryName},${categoryIds}`;
      }
    });
  });
  if (Object.keys(categoriesEntities).length > 0) {
    newConfig.categories = newConfig.categories || {};
    newConfig.categories.entities = categoriesEntities;
  }

  // details.<entity> → EntityTypeConfig (fields + display.detail)
  newConfig.details = newConfig.details || {};
  v2EntityData.forEach(entityType => {
    if (entityType.config) {
      newConfig.details[entityType.name] = translateV2DetailsConfig(
        entityType.config,
        entityType.label,
        categoryValues,
        strippedConfig.category_types,
        publicConfig,
        entityType.max_owners === 1
      );
    }
  });

  newConfig.logs = newConfig.logs || {};
  newConfig.logs.tooltips = newConfig.logs.tooltips || {};
  // logs.tooltips.header.<entity> → EntityTypeConfig.display.tooltip
  newConfig.logs.tooltips.header = newConfig.logs.tooltips.header || {};
  v2EntityData.forEach(entityType => {
    const fields = entityType.config?.fields || {};
    const segmentFields = entityType.config?.segment_fields || {};
    const tooltipConfig = entityType.config?.display?.tooltip || {};
    if (tooltipConfig.title_field) {
      const titleConfig = resolveFieldRefDisplay(
        {
          type: 'field',
          field: tooltipConfig.title_field,
          label: tooltipConfig.title_field_label
        },
        fields,
        segmentFields,
        entityType.max_owners === 1
      );
      if (titleConfig) {
        newConfig.logs.tooltips.header[entityType.name] = {
          field: titleConfig.name,
          label: titleConfig.label
        };
      }
    }
  });

  // logs.tooltips.body.<entity> → EntityTypeConfig.display.tooltip
  newConfig.logs.tooltips.body = newConfig.logs.tooltips.body || {};
  v2EntityData.forEach(entityType => {
    const fields = entityType.config?.fields || {};
    const segmentFields = entityType.config?.segment_fields || {};
    const tooltipConfig = entityType.config?.display?.tooltip || {};
    const body = tooltipConfig.body || [];
    const entitySingleOwner = entityType.max_owners === 1;
    if (body.length > 0) {
      newConfig.logs.tooltips.body[entityType.name] = {};
      body.forEach((tooltipDisplayRef, index) => {
        if (typeof tooltipDisplayRef === 'object' && tooltipDisplayRef.type === 'triptych') {
          const triptychConfig = {
            type: 'triptych'
          };
          const category = tooltipDisplayRef.additional_category;
          if (category) {
            const categoryName = category[0] === '|' ? category.slice(1) : category;
            triptychConfig['status-name'] = categoryName;
          }
          newConfig.logs.tooltips.body[entityType.name][`row${index + 1}`] = triptychConfig;
        } else {
          const displayConfig = resolveFieldRefDisplay(tooltipDisplayRef, fields, segmentFields, entitySingleOwner);
          if (displayConfig) {
            if (displayConfig.type === 'Category') {
              newConfig.logs.tooltips.body[entityType.name][`row${index + 1}`] = {
                type: 'category',
                'category-name': displayConfig.name
              };
            } else {
              newConfig.logs.tooltips.body[entityType.name][`row${index + 1}`] = {
                type: displayConfig.type,
                'field-name': displayConfig.name
              };
            }
          }
        }
      });
    }
  });

  // dashboard.list.paths → add entity names after 'map' (or at front)
  const listPaths = newConfig.dashboard?.list?.paths;
  if (Array.isArray(listPaths)) {
    const mapIdx = listPaths.indexOf('map');
    listPaths.splice(mapIdx >= 0 ? mapIdx + 1 : 0, 0, ...entityNames);
  }

  newConfig.dashboard = newConfig.dashboard || {};
  newConfig.dashboard.list = newConfig.dashboard.list || {};
  newConfig.dashboard.list.datatable = newConfig.dashboard.list.datatable || {};
  // dashboard.list.datatable.columns.<entity>         → display.dashboard
  // dashboard.list.datatable.columns.overlap.<entity> → display.overlap_dashboard
  newConfig.dashboard.list.datatable.columns = newConfig.dashboard.list.datatable.columns || {};
  const columns = newConfig.dashboard?.list?.datatable?.columns;
  v2EntityData.forEach(entityType => {
    if (entityType.config) {
      columns[entityType.name] = translateV2DashboardConfig(entityType.config, entityType.max_owners === 1);
      if (columns[entityType.name][0] && !columns[entityType.name].some(({styleName}) => styleName === 'strong')) {
        // First allowable column is default bold
        const defaultColumn = columns[entityType.name].find(({styleName = null}) => !styleName);
        defaultColumn.styleName = 'strong';
      }
    }
  });
  if (v2EntityData.some(entityType => entityType.config?.display?.overlap_dashboard)) {
    columns.overlap = columns.overlap || {};
    v2EntityData.forEach(entityType => {
      if (entityType.config?.display?.overlap_dashboard) {
        columns.overlap[entityType.name] = translateV2DashboardConfig(
          entityType.config,
          entityType.max_owners === 1,
          newConfig.entity_types,
          entityType.config?.display?.overlap_dashboard,
          true
        );
        if (
          columns.overlap[entityType.name][0] &&
          !columns.overlap[entityType.name].some(({styleName}) => styleName === 'strong')
        ) {
          // First allowable column is default bold
          const defaultColumn = columns.overlap[entityType.name].find(({styleName = null}) => !styleName);
          defaultColumn.styleName = 'strong';
        }
      }
    });
  }

  // dashboard.list.datatable.config.<entity>         → filters.dashboard defaults
  // dashboard.list.datatable.config.overlap.<entity> → filters.overlap_dashboard defaults
  newConfig.filters = newConfig.filters || {};
  newConfig.dashboard.list.datatable.config = newConfig.dashboard.list.datatable.config || {};
  const datatableConfig = newConfig.dashboard.list.datatable.config;
  v2EntityData.forEach(entityType => {
    const defaults = translateFilterDefaults(
      entityType.config,
      entityType.config?.filters?.dashboard,
      agencyTypes,
      categoryValues,
      strippedConfig.category_types,
      globalFilters
    );
    if (Object.keys(defaults).length > 0) {
      datatableConfig[entityType.name] = defaults;
    } else {
      datatableConfig[entityType.name] = {};
    }
  });
  if (v2EntityData.some(entityType => entityType.config?.filters?.overlap_dashboard?.length)) {
    datatableConfig.overlap = datatableConfig.overlap || {};
    v2EntityData.forEach(entityType => {
      const defaults = translateFilterDefaults(
        entityType.config,
        entityType.config?.filters?.overlap_dashboard,
        agencyTypes,
        categoryValues,
        strippedConfig.category_types,
        globalFilters
      );
      if (Object.keys(defaults).length > 0) {
        datatableConfig.overlap[entityType.name] = defaults;
      } else {
        datatableConfig.overlap[entityType.name] = {};
      }
    });
  }

  // dashboard.list['top-bar'].<entity> → presence derived at runtime
  newConfig.dashboard.list['top-bar'] = newConfig.dashboard.list['top-bar'] || {};
  const topBar = newConfig.dashboard.list['top-bar'];
  v2EntityData.forEach(entityType => {
    if (entityType.config?.display?.hide_add) {
      return;
    }
    topBar[entityType.name] = {
      link: {to: `/${entityType.name}/new`},
      permission: 'add'
    };
  });

  // dashboard.list.menu → restore entity items into the first group with items
  newConfig.dashboard.list.menu = newConfig.dashboard.list.menu || [];
  const menuGroups = newConfig.dashboard.list.menu;
  if (Array.isArray(menuGroups)) {
    const entityItems = (v2EntityData
      .filter(entityType => entityType.config?.display?.show_in_library ?? true)
      .map(entityType => ({
        type: entityType.name,
        label: entityType.label,
        icon: '[entity]'
      }))
    );
    const overlapItems = (v2EntityData
      .filter(entityType => entityType.config?.display?.overlap_dashboard)
      .map(entityType => ({
        type: entityType.name,
        label: entityType.label
      }))
    );
    if (overlapItems.length > 0) {
      entityItems.push({
        type: 'overlaps',
        label: 'Overlaps',
        icon: 'warning',
        children: overlapItems
      });
    }
    menuGroups[0].items = [...entityItems, ...menuGroups[0].items];
  }

  // filters.map.<entity>Filters → EntityTypeConfig.filters.map
  if (v2EntityData.some(entityType => entityType.config?.filters?.map?.length)) {
    newConfig.filters = newConfig.filters || {};
    newConfig.filters.map = newConfig.filters.map || {};
    v2EntityData.forEach(entityType => {
      if (entityType.config?.filters?.map?.length > 0) {
        const filters = {
          category: ['categories__in'],
          map_category: ['categories__in']
        };
        entityType.config?.filters?.map?.forEach(mapFilterItem => {
          if (typeof mapFilterItem === 'object') {
            if (mapFilterItem.type === 'map_agency_combo') {
              filters.agency = ['agency__in'];
              filters.agency_type = ['agency__type__in'];
            } else if (mapFilterItem.type === 'agency') {
              filters.agency = ['agency__in'];
            } else if (mapFilterItem.type === 'agency_type') {
              filters.agency_type = ['agency__type__in'];
            } else if (mapFilterItem.type === 'owners') {
              filters.owners = ['owners__in'];
            } else if (mapFilterItem.type === 'field') {
              if (mapFilterItem.field[0] !== '|') {
                filters[mapFilterItem.field] = [`attrs__${mapFilterItem.field}`];
              }
            }
          } else if (mapFilterItem[0] !== '|') {
            filters[mapFilterItem] = [`attrs__${mapFilterItem}`];
          }
        });
        newConfig.filters.map[`${entityType.name}Filters`] = filters;
      }
    });
  }

  // filters.entities.<entity> → MapFilterItem.default values
  newConfig.filters = newConfig.filters || {};
  newConfig.filters.entities = newConfig.filters.entities || {};
  const filterEntityDefaults = {};
  v2EntityData.forEach(entityType => {
    const defaults = translateFilterDefaults(
      entityType.config,
      entityType.config?.filters?.map,
      agencyTypes,
      categoryValues,
      strippedConfig.category_types,
      globalFilters
    );
    if (Object.keys(defaults).length > 0) {
      filterEntityDefaults[entityType.name] = defaults;
    }
  });
  newConfig.filters.entities = { ...newConfig.filters.entities, ...filterEntityDefaults };

  // filters.filters[] → add entity filter panel sections (name === 'entity')
  if (Array.isArray(newConfig.filters?.filters)) {
    const mapFilterEntities = [];
    v2EntityData.forEach(entityType => {
      if (entityType.config?.filters?.map || entityType.config?.filters?.map_default_on) {
        mapFilterEntities.push({
          name: 'entity',
          type: entityType.name,
          label: entityType.config?.filters?.map_label || entityType.label,
          toggled: entityType.config?.filters?.map_default_on,
          sections: translateV2MapFilterSections(
            entityType,
            categoryValues,
            strippedConfig.category_types,
            entityType.max_owners === 1
          )
        });
      }
    });
    const newFilters = [...newConfig.filters.filters];
    const insertIndex = newFilters.findIndex(filterItem => (
      filterItem.name !== 'date' && filterItem.name !== 'sep'
    ));
    if (insertIndex === -1) {
      newFilters.splice(newFilters.length, 0, ...mapFilterEntities);
    } else {
      newFilters.splice(insertIndex, 0, ...mapFilterEntities);
    }
    newConfig.filters.filters = newFilters;
  }

  // filters.dashboard.<entity>         → EntityTypeConfig.filters.dashboard
  // filters.dashboard.overlap.<entity> → EntityTypeConfig.filters.overlap_dashboard
  if (v2EntityData.some(entityType => entityType.config?.filters?.dashboard?.length)) {
    newConfig.filters = newConfig.filters || {};
    newConfig.filters.dashboard = newConfig.filters.dashboard || {};
    v2EntityData.forEach(entityType => {
      if (entityType.config?.filters?.dashboard?.length > 0) {
        newConfig.filters.dashboard[entityType.name] = translateV2DashboardFilterConfig(
          entityType.config,
          entityType.config?.filters?.dashboard,
          categoryValues,
          strippedConfig.category_types,
          entityType.max_owners === 1
        );
      }
    });
  }
  if (v2EntityData.some(entityType => entityType.config?.filters?.overlap_dashboard?.length)) {
    newConfig.filters = newConfig.filters || {};
    newConfig.filters.dashboard = newConfig.filters.dashboard || {};
    newConfig.filters.dashboard.overlap = newConfig.filters.dashboard.overlap || {};
    v2EntityData.forEach(entityType => {
      if (entityType.config?.filters?.overlap_dashboard?.length > 0) {
        newConfig.filters.dashboard.overlap[entityType.name] = translateV2DashboardFilterConfig(
          entityType.config,
          entityType.config?.filters?.overlap_dashboard,
          categoryValues,
          strippedConfig.category_types,
          entityType.max_owners === 1
        );
      }
    });
  }

  // api.serializer.extra_kwargs.<entity> → EntityTypeConfig.fields validation config
  // skip restoring extra_kwargs. It is unused by the frontend.

  // autocomplete.map.<entity> / autocomplete.overlap.<entity>
  //   → EntityTypeConfig.fields[*].use_in_autocomplete
  // skip restoring use_in_autocomplete. It is unused by the frontend.

  // batch.<entity>.normalizer → boolean_fields, key_map, category_map, owners_key
  // skip restoring normalizer. It is unused by the frontend.

  return newConfig;
}
